import React from "react";
import dayjs from "dayjs";
import { formatCurrency, formatNumber } from "../../../utils/helpers";

/*
 * Historial de movimientos de un producto, a partir de un elemento de
 * `reportes?reporte=operaciones-inventario`. Lo comparten la pestaña de
 * movimientos del producto (Ajustes/index.js) y el reporte impreso
 * (Print/ReporteOperacionesInventario.js), que deben mostrar lo mismo.
 */
export const armarMovimientos = (producto) => {
    const operaciones = [
        ...producto.ajustes.map(operacion => ({ ...operacion, tipo: 'ajuste' })),
        ...producto.facturas.map(operacion => ({ ...operacion, tipo: 'factura' })),
        ...producto.compras.map(operacion => ({ ...operacion, tipo: 'compra' })),
    ].map(operacion => {
        let tipoOperacion = (operacion.incrementar === 1 || operacion.compra) ? "ENTRADA" : "SALIDA";
        if (operacion.cantidad == 0) tipoOperacion = 'IGUAL';
        const estado = operacion.factura?.estado || operacion.compra?.estado || "completada";
        // Solo ventas: la compra cancelada conserva su ENTRADA, y su
        // salida es el ajuste "Cancelación de compra".
        if (estado === 'cancelada' && operacion.tipo === 'factura') {
            tipoOperacion = 'CANCELADA';
        }
        const entrada = tipoOperacion === 'ENTRADA';
        const salida = tipoOperacion === 'SALIDA';
        const factura = operacion.tipo === 'factura';
        const compra = operacion.tipo === 'compra';
        const ajuste = operacion.tipo === 'ajuste';

        return {
            id: `${operacion.tipo}-${operacion.id}`,
            fecha: dayjs(operacion.compra?.fecha || operacion.factura?.fecha || operacion.created_at).format("YYYY-MM-DD"),
            // Orden del historial: cuándo se movió el stock, o sea la línea.
            // Editar una compra o factura recrea sus líneas, así que la
            // cabecera puede ser días anterior y el saldo pasar por negativo.
            created_at: operacion.created_at || operacion.compra?.created_at || operacion.factura?.created_at,
            operacion: tipoOperacion,
            costo: operacion.costo || operacion.costo_total || 0,
            // En unidad base: el saldo se reconstruye restando estas
            // cantidades de la existencia, que está en base.
            cantidad: (operacion.cantidad_base ?? operacion.cantidad) || operacion.detalle?.cantidad || 0,
            entradas: entrada ? (operacion.cantidad_base ?? operacion.cantidad) : "-",
            salidas: salida ? (operacion.cantidad_base ?? operacion.cantidad) || operacion.detalle.cantidad : "-",
            contacto: factura ? operacion.factura?.cliente : (compra ? operacion.compra?.contacto : null),
            usuario: operacion.compra?.usuario || operacion.factura?.usuario || operacion.usuario,
            observacion: factura ? "Venta " + operacion.factura?.numero_comprobante :
                (ajuste && (operacion.nota != null || operacion.crear)) ? operacion.descripcion :
                (ajuste && !operacion.nota) ? "Ajuste Manual - " + operacion.descripcion :
                compra ? "Compra  " + (operacion.compra.ncf || operacion.compra.numero || '') + (operacion.compra.estado === 'cancelada' ? ' (cancelada)' : '') : '',
        };
    });

    // Del más reciente al más viejo, reconstruyendo la existencia hacia atrás.
    operaciones.sort((a, b) => dayjs(b.created_at) - dayjs(a.created_at));

    let disponible = parseFloat(producto.cantidad);
    operaciones.forEach(operacion => {
        operacion.disponible = disponible;
        if (operacion.operacion === 'ENTRADA') {
            disponible -= parseFloat(operacion.cantidad);
        } else if (operacion.operacion === 'SALIDA') {
            disponible += parseFloat(operacion.cantidad);
        }
    });

    return operaciones;
};

/** El filtro de "Buscar" y "Operación" de la pestaña de movimientos. */
export const filtrarMovimientos = (movimientos, filtro) => {
    if (!filtro) {
        return movimientos;
    }

    return movimientos.filter(movimiento => {
        return movimiento.observacion?.includes(filtro) || movimiento.operacion.includes(filtro);
    });
};

export const TablaMovimientos = ({ movimientos, inventariable, className = "table tickets-table" }) => (
    <table className={className}>
        <thead>
            <tr>
                <th>Fecha</th>
                <th>Operación</th>
                <th>Entradas</th>
                <th>Salidas</th>
                <th>Descripción</th>
                <th className="text-center">En Inventario</th>
                <th>Monto</th>
            </tr>
        </thead>
        <tbody>
            {movimientos.map((movimiento) => (
                <tr key={movimiento.id}>
                    <td>{movimiento.fecha}</td>
                    <td>
                        {movimiento.operacion}<br/>
                        <small>{`${movimiento.usuario?.nombre} ${movimiento.usuario?.apellido}`.trim()}</small>
                    </td>
                    <td>{movimiento.entradas === '-' ? movimiento.entradas : formatNumber(movimiento.entradas)}</td>
                    <td>{movimiento.salidas === '-' ? movimiento.salidas : formatNumber(movimiento.salidas)}</td>
                    <td>
                        {movimiento.contacto && <><small>{movimiento.contacto.codigo} - {movimiento.contacto.nombre}</small><br/></>}
                        {movimiento.observacion}
                    </td>
                    <td className="text-center">{inventariable ? formatNumber(movimiento.disponible) : "∞"}</td>
                    <td>{formatCurrency(movimiento.costo)}</td>
                </tr>
            ))}
        </tbody>
    </table>
);
